import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";
import { CollapsibleSection } from "./collapsible-section";
import { MessageSources } from "./message-sources";
import { SelectionContextChip } from "./selection-context-chip";
import type { ChatMessage } from "./types";

interface ChatBubbleProps {
  message: ChatMessage;
  prevRole?: ChatMessage["role"];
}

export const ChatBubble = ({ message, prevRole }: ChatBubbleProps) => {
  const isUser = message.role === "user";
  const isTurnChange = prevRole !== undefined && prevRole !== message.role;
  const spacingClasses = {
    "mt-3": prevRole !== undefined && !isTurnChange,
    "mt-8": isTurnChange,
  };

  if (isUser) {
    return (
      <div
        className={cn("flex w-full flex-col items-end gap-1", spacingClasses)}
      >
        {message.selectionContext && (
          <span className="max-w-[85%]">
            <SelectionContextChip context={message.selectionContext} />
          </span>
        )}
        <div className="max-w-[85%] rounded-lg border border-primary px-3 py-2.5">
          <Typography
            variant="bodyW2"
            className="whitespace-pre-wrap break-words"
          >
            {message.content}
          </Typography>
        </div>
      </div>
    );
  }

  if (message.error) {
    return (
      <div className={cn("flex w-full justify-start", spacingClasses)}>
        <div className="max-w-[85%] rounded-lg border border-destructive bg-red-10 px-3 py-2.5">
          <Typography variant="bodyW2" className="break-words">
            {message.content}
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex w-full", spacingClasses)}>
      <div className="min-w-0 flex-1">
        <ChatMarkdown>{message.content}</ChatMarkdown>

        {message.steps && message.steps.length > 0 && (
          <CollapsibleSection
            label={`What I checked (${message.steps.length})`}
          >
            <Stack gap="xs">
              {message.steps.map((step, index) => (
                <Typography
                  key={`${step}-${index}`}
                  variant="captionW1"
                  textColor="tertiary"
                >
                  {step}
                </Typography>
              ))}
            </Stack>
          </CollapsibleSection>
        )}

        {message.sources && message.sources.length > 0 && (
          <MessageSources sources={message.sources} />
        )}
      </div>
    </div>
  );
};
